"use client";

import { Suspense, useEffect, useMemo, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { Bold, ChevronLeft, Image as ImageIcon, Italic, Link2, List, Loader2, Quote, X } from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { Textarea } from "@/app/_module/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/_module/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import { useToast } from "@/app/_module/components/ui/use-toast";
import { forumService, forumPath } from "../_modules/components/ForumService";
import { ForumMarkdown } from "../_modules/components/ForumMarkdown";
import { CategoryIconTile } from "../_modules/components/categoryIcons";
import { useForumAccess } from "../_modules/hooks/useForumAccess";
import { FORUM_IMAGE_UPLOADS_ENABLED } from "@/app/_module/utils/featureFlags";
import { checkContentOnDevice, ContentCheck } from "@/app/_module/services/localContentCheck";

const DRAFT_KEY = "forum-draft";

function SubmitPageInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { toast } = useToast();
  const { canWrite, requireWrite, isModerator } = useForumAccess();

  const [categoryId, setCategoryId] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [images, setImages] = useState<{ path: string; url: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // On-device InsuraBot pre-check (free, never leaves the browser). Advisory:
  // the author can fix the text or post anyway; the server rails still decide.
  const [precheck, setPrecheck] = useState<ContentCheck | null>(null);
  const [precheckAcknowledged, setPrecheckAcknowledged] = useState(false);

  const { data: categories } = useQuery({
    queryKey: ["forum", "categories"],
    queryFn: forumService.getCategories,
    staleTime: 10 * 60_000,
  });
  const selectable = useMemo(
    () => (categories || []).filter((c) => !c.isAdminPostOnly || isModerator),
    [categories, isModerator],
  );

  // Draft restore + category preselect (?category=slug).
  useEffect(() => {
    try {
      const draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
      if (draft?.title) setTitle(draft.title);
      if (draft?.body) setBody(draft.body);
    } catch {}
  }, []);
  useEffect(() => {
    const slug = searchParams.get("category");
    if (slug && selectable.length && !categoryId) {
      const match = selectable.find((c) => c.slug === slug);
      if (match) setCategoryId(match.id);
    }
  }, [searchParams, selectable, categoryId]);
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ title, body }));
      } catch {}
    }, 400);
    return () => clearTimeout(t);
  }, [title, body]);

  // Mini markdown toolbar: wraps the current selection.
  const wrapSelection = (before: string, after = before) => {
    const el = document.getElementById("forum-body") as HTMLTextAreaElement | null;
    if (!el) return;
    const { selectionStart: s, selectionEnd: e } = el;
    const selected = body.slice(s, e) || "text";
    setBody(body.slice(0, s) + before + selected + after + body.slice(e));
  };

  const addImage = async (file: File) => {
    if (images.length >= 5) {
      toast({ title: "Max 5 images per post", variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      const res = await forumService.uploadImage(file);
      setImages((prev) => [...prev, { path: res.path, url: res.url }]);
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message, variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const submit = () =>
    requireWrite(async () => {
      if (!categoryId || title.trim().length < 3) {
        toast({ title: "Pick a category and write a title", variant: "destructive", duration: 3000 });
        return;
      }
      setSubmitting(true);
      if (!precheckAcknowledged) {
        const verdict = await checkContentOnDevice(title.trim(), body.trim());
        if (verdict && (verdict.pii || verdict.promo)) {
          setPrecheck(verdict);
          setSubmitting(false);
          return;
        }
      }
      try {
        const res = await forumService.createPost({
          categoryId,
          title: title.trim(),
          body: body.trim() || undefined,
          imagePaths: images.map((i) => i.path),
        });
        try {
          localStorage.removeItem(DRAFT_KEY);
        } catch {}
        toast({ title: "Posted 🔥", duration: 2500 });
        router.push(forumPath(`/t/${res.slug}`));
      } catch (e: any) {
        toast({ title: "Couldn't post", description: e.message, variant: "destructive", duration: 6000 });
      } finally {
        setSubmitting(false);
      }
    });

  return (
    <div className="mx-auto max-w-2xl">
      <button
        type="button"
        onClick={() => router.back()}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
      >
        <ChevronLeft className="h-3.5 w-3.5" /> Back
      </button>
      <h1 className="mt-1 text-lg font-semibold">Create a post</h1>
      <p className="text-xs text-muted-foreground">
        You&apos;re posting to the global InsuraCentral agent community under your real name.
      </p>
      <ul className="mt-2 grid gap-1 rounded-md border bg-muted/40 px-3 py-2 text-[11px] leading-4 text-muted-foreground sm:grid-cols-3">
        <li><span className="font-semibold text-foreground/80">No client identifiers.</span> Describe cases by age, state and condition. Never names with details, SSNs, dates of birth, Medicare IDs, policy numbers, addresses, or a client&apos;s phone or email.</li>
        <li><span className="font-semibold text-foreground/80">No pitching.</span> No recruiting, contract offers, lead-vendor ads, courses, affiliate links or &ldquo;DM me&rdquo;. Recommend, don&apos;t sell.</li>
        <li><span className="font-semibold text-foreground/80">Links unlock</span> after your first week or 5 rep. Posts are screened before they go up.</li>
      </ul>

      <div className="mt-4 space-y-3">
        <Select value={categoryId} onValueChange={setCategoryId}>
          <SelectTrigger className="w-full sm:w-72">
            <SelectValue placeholder="Choose a category" />
          </SelectTrigger>
          <SelectContent>
            {selectable.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                <span className="inline-flex items-center gap-2">
                  <CategoryIconTile slug={c.slug} size="sm" />
                  {c.name}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, 300))}
            placeholder="Title — make it worth the click"
            className="text-sm font-medium"
            maxLength={300}
          />
          <div className="mt-0.5 text-right text-[10px] text-muted-foreground">{title.length}/300</div>
        </div>

        <Tabs defaultValue="write">
          <div className="flex items-center justify-between">
            {/* The shared TabsList sizes its sliding indicator from the active trigger's
                height, so the list and the triggers have to agree: 36px list, 4px padding,
                28px triggers. h-8 with the default py-2 triggers hung the indicator below
                the list. */}
            <TabsList className="h-9 p-1">
              <TabsTrigger value="write" className="h-7 px-3 py-0 text-xs">Write</TabsTrigger>
              <TabsTrigger value="preview" className="h-7 px-3 py-0 text-xs">Preview</TabsTrigger>
            </TabsList>
            <div className="flex gap-0.5">
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => wrapSelection("**")} aria-label="Bold">
                <Bold className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => wrapSelection("*")} aria-label="Italic">
                <Italic className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => wrapSelection("[", "](https://)")} aria-label="Link">
                <Link2 className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => wrapSelection("\n> ", "")} aria-label="Quote">
                <Quote className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => wrapSelection("\n- ", "")} aria-label="List">
                <List className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          <TabsContent value="write" className="mt-2">
            <Textarea
              id="forum-body"
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, 40_000))}
              placeholder="Your story, question, or win… (markdown supported)"
              rows={10}
              className="text-sm resize-y"
            />
          </TabsContent>
          <TabsContent value="preview" className="mt-2">
            <div className="min-h-[160px] rounded-md border p-3">
              {body.trim() ? <ForumMarkdown>{body}</ForumMarkdown> : <p className="text-xs text-muted-foreground">Nothing to preview yet.</p>}
            </div>
          </TabsContent>
        </Tabs>

        {FORUM_IMAGE_UPLOADS_ENABLED && (
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex">
            <input
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) addImage(f);
                e.currentTarget.value = "";
              }}
            />
            <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" asChild>
              <span>
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
                Add image ({images.length}/5)
              </span>
            </Button>
          </label>
          {images.map((img) => (
            <div key={img.path} className="relative">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={img.url} alt="" className="h-14 w-14 rounded-md border object-cover" />
              <button
                type="button"
                onClick={() => setImages((prev) => prev.filter((i) => i.path !== img.path))}
                className="absolute -right-1.5 -top-1.5 rounded-full bg-destructive p-0.5 text-white shadow"
                aria-label="Remove image"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
        )}

        {precheck && (precheck.pii || precheck.promo) && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-100" role="alert">
            <p className="font-semibold">
              InsuraBot read this on your device and thinks it {precheck.pii ? "identifies a real client" : "reads as a pitch"}
              {precheck.reason ? `: ${precheck.reason}` : "."}
            </p>
            <p className="mt-1">
              {precheck.pii
                ? "Keep it to age, state and condition. Names with details, contact info and policy numbers are blocked on the floor."
                : "Recruiting, contract offers, vendor ads and \"DM me\" are blocked on the floor. Ask or recommend instead."}
            </p>
            <div className="mt-2 flex gap-2">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setPrecheck(null); setPrecheckAcknowledged(false); }}>
                Edit the post
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setPrecheck(null); setPrecheckAcknowledged(true); }}>
                Post anyway
              </Button>
            </div>
          </div>
        )}

        <div className="sticky bottom-2 flex justify-end gap-2 rounded-lg border bg-background/95 backdrop-blur p-2 shadow-sm">
          <Button size="sm" variant="ghost" onClick={() => router.back()}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={submitting || !categoryId || title.trim().length < 3 || (!FORUM_IMAGE_UPLOADS_ENABLED && !body.trim()) || !canWrite}>
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
            Post
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function ForumSubmitPage() {
  return (
    <Suspense fallback={null}>
      <SubmitPageInner />
    </Suspense>
  );
}
